import { LitElement, html, css } from "lit";
import { customElement, state } from "lit/decorators.js";
import "@shoelace-style/shoelace/dist/components/card/card.js";
import "@shoelace-style/shoelace/dist/components/button/button.js";
import "@shoelace-style/shoelace/dist/components/alert/alert.js";
import "@shoelace-style/shoelace/dist/components/icon/icon.js";
import "@shoelace-style/shoelace/dist/components/spinner/spinner.js";
import "@shoelace-style/shoelace/dist/components/progress-bar/progress-bar.js";
import "@shoelace-style/shoelace/dist/components/radio-group/radio-group.js";
import "@shoelace-style/shoelace/dist/components/radio-button/radio-button.js";
import "@shoelace-style/shoelace/dist/components/badge/badge.js";
import "../components/app-shell";
import { getDb, saveCgmReadingsBatch, saveEntry, updateEntry } from "../db";
import {
  type LibreViewRow,
  type LibreViewValidation,
  type DexcomRow,
  type DexcomValidation,
  type ReconciliationResult,
  type ReconciliationMatch,
  type LogEntry,
  DAFNE_LABELS,
  parseLibreViewHeader,
  parseLibreViewRow,
  libreViewToCgmReadings,
  parseDexcomHeader,
  parseDexcomRow,
  buildDexcomColumnIndices,
  dexcomToCgmReadings,
  reconcileLibreViewData,
  formatInsulinDose,
  suggestPeriodTagSmart,
  DEFAULT_MEAL_TIMES,
  PeriodTag,
  type MealTimes,
} from "../types";
import { format } from "date-fns";

type FileFormat = "libre" | "dexcom";

// Local storage key for remembering format selection
const FORMAT_STORAGE_KEY = "dafne_cgm_import_format";

interface ImportResult {
  success: boolean;
  message: string;
  recordsProcessed: number;
  recordsImported: number;
  recordsSkipped: number;
  dateRange?: {
    start: Date;
    end: Date;
  };
}

// Unified validation type for both formats
type ValidationResult = (LibreViewValidation | DexcomValidation) & {
  glucoseUnit?: "mg/dL" | "mmol/L";
};

@customElement("upload-page")
export class UploadPage extends LitElement {
  @state() private uploading = false;
  @state() private validating = false;
  @state() private validation: ValidationResult | null = null;
  @state() private importResult: ImportResult | null = null;
  @state() private selectedFile: File | null = null;
  @state() private progress = 0;
  @state() private fileFormat: FileFormat = "libre";
  @state() private existingDataInfo: {
    count: number;
    dateRange?: { start: Date; end: Date };
  } | null = null;

  // Reconciliation state
  @state() private showReconciliation = false;
  @state() private reconciliationResult: ReconciliationResult | null = null;
  @state() private reconciliationDecisions: Map<
    number,
    "accept" | "skip" | "edit"
  > = new Map();
  @state() private parsedLibreViewRows: LibreViewRow[] = [];
  @state() private reconciling = false;
  @state() private reconciliationMealTimes: MealTimes = DEFAULT_MEAL_TIMES;

  static styles = css`
    :host {
      display: block;
    }

    .upload-content {
      padding: var(--page-padding, 1rem);
      max-width: 600px;
      margin: 0 auto;
    }

    /* Expand to full width on tablet/desktop */
    @media (min-width: 768px) {
      .upload-content {
        max-width: none;
        padding: 1.5rem 2rem;
      }
    }

    @media (min-width: 1200px) {
      .upload-content {
        padding: 2rem 3rem;
      }
    }

    .page-header {
      margin-bottom: 1.5rem;
      display: none;
    }

    .page-header h1 {
      font-size: 1.5rem;
      font-weight: 600;
      margin: 0 0 0.5rem;
    }

    .page-header p {
      color: #6c757d;
      margin: 0;
    }

    /* Show page header only on large screens where app-shell header is hidden */
    @media (min-width: 1024px) {
      .page-header {
        display: block;
      }
    }

    sl-card {
      margin-bottom: 1rem;
      width: 100%;
      display: block;
    }

    sl-card::part(base) {
      max-width: none;
      width: 100%;
    }

    sl-card::part(body) {
      padding: 1.25rem;
    }

    @media (min-width: 768px) {
      sl-card::part(body) {
        padding: 1.5rem;
      }
    }

    @media (min-width: 1024px) {
      sl-card::part(body) {
        padding: 2rem;
      }
    }

    .upload-section {
      text-align: center;
      padding: 2rem;
      border: 2px dashed #dee2e6;
      border-radius: 0.5rem;
      cursor: pointer;
      transition: all 0.2s;
    }

    @media (min-width: 768px) {
      .upload-section {
        padding: 3rem 2rem;
      }

      .upload-section sl-icon {
        font-size: 4rem;
      }

      .upload-section h3 {
        font-size: 1.25rem;
      }
    }

    @media (min-width: 1024px) {
      .upload-section {
        padding: 4rem 2rem;
      }
    }

    .upload-section:hover {
      border-color: #0d6efd;
      background: #f8f9fa;
    }

    .upload-section.dragging {
      border-color: #0d6efd;
      background: #e7f1ff;
    }

    .upload-section sl-icon {
      font-size: 3rem;
      color: #6c757d;
      margin-bottom: 1rem;
    }

    .upload-section h3 {
      margin: 0 0 0.5rem;
      font-weight: 500;
    }

    .upload-section p {
      margin: 0;
      color: #6c757d;
      font-size: 0.875rem;
    }

    .file-input {
      display: none;
    }

    .selected-file {
      display: flex;
      align-items: center;
      gap: 1rem;
      padding: 1rem;
      background: #f8f9fa;
      border-radius: 0.5rem;
      margin-top: 1rem;
    }

    .selected-file sl-icon {
      font-size: 1.5rem;
      color: #0d6efd;
    }

    .file-info {
      flex: 1;
    }

    .file-info .name {
      font-weight: 500;
    }

    .file-info .size {
      font-size: 0.75rem;
      color: #6c757d;
    }

    .validation-results {
      margin-top: 1rem;
    }

    .validation-item {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.5rem 0;
    }

    .validation-item sl-icon {
      margin-top: 0.125rem;
    }

    .validation-item.error sl-icon {
      color: #dc3545;
    }

    .validation-item.warning sl-icon {
      color: #ffc107;
    }

    .validation-item.success sl-icon {
      color: #198754;
    }

    .actions {
      display: flex;
      gap: 1rem;
      margin-top: 1.5rem;
    }

    .actions sl-button {
      flex: 1;
    }

    .import-summary {
      background: #f8f9fa;
      border-radius: 0.5rem;
      padding: 1rem;
    }

    .import-summary .stat {
      display: flex;
      justify-content: space-between;
      padding: 0.25rem 0;
    }

    .import-summary .stat .label {
      color: #6c757d;
    }

    .existing-data {
      background: #e7f1ff;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .existing-data h4 {
      margin: 0 0 0.5rem;
      font-size: 0.875rem;
      font-weight: 600;
    }

    .existing-data p {
      margin: 0;
      font-size: 0.875rem;
      color: #495057;
    }

    .libreview-info {
      background: #fff3cd;
      border-radius: 0.5rem;
      padding: 1rem;
      margin-bottom: 1rem;
    }

    .libreview-info h4 {
      margin: 0 0 0.5rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: #856404;
    }

    .libreview-info ol {
      margin: 0;
      padding-left: 1.25rem;
      font-size: 0.875rem;
      color: #856404;
    }

    .libreview-info li {
      margin-bottom: 0.25rem;
    }

    .notation-guide {
      margin-bottom: 1rem;
    }

    .notation-guide details {
      background: #e7f5ff;
      border-radius: 0.5rem;
      overflow: hidden;
    }

    .notation-guide summary {
      padding: 0.75rem 1rem;
      font-size: 0.875rem;
      font-weight: 600;
      color: #1864ab;
      cursor: pointer;
      user-select: none;
    }

    .notation-guide summary:hover {
      background: #d0ebff;
    }

    .notation-guide .guide-content {
      padding: 0 1rem 1rem;
      font-size: 0.875rem;
      color: #1864ab;
    }

    .notation-guide h5 {
      font-size: 0.8rem;
      font-weight: 600;
      margin: 0.75rem 0 0.5rem;
      color: #1971c2;
    }

    .notation-guide h5:first-child {
      margin-top: 0;
    }

    .notation-guide code {
      background: #d0ebff;
      padding: 0.15rem 0.35rem;
      border-radius: 0.25rem;
      font-size: 0.8rem;
    }

    .notation-guide ul {
      margin: 0;
      padding-left: 1.25rem;
    }

    .notation-guide li {
      margin-bottom: 0.25rem;
    }

    .format-selector {
      margin-bottom: 1rem;
    }

    .format-selector label {
      display: block;
      font-weight: 600;
      margin-bottom: 0.5rem;
      font-size: 0.875rem;
    }

    sl-radio-group::part(fieldset) {
      border: none;
      padding: 0;
      margin: 0;
    }

    sl-progress-bar {
      margin: 1rem 0;
    }

    /* Reconciliation responsive layout */
    .match-list {
      display: grid;
      grid-template-columns: 1fr;
      gap: 0;
    }

    .match-item {
      border-bottom: 1px solid #dee2e6;
    }

    .match-item:last-child {
      border-bottom: none;
    }

    @media (min-width: 768px) {
      .match-list {
        grid-template-columns: repeat(2, 1fr);
        gap: 1rem;
        max-height: 600px !important;
      }

      .match-item {
        border: 1px solid #dee2e6;
        border-radius: 8px;
        margin: 0;
      }

      .match-item:last-child {
        border: 1px solid #dee2e6;
      }
    }

    @media (min-width: 1024px) {
      .match-list {
        grid-template-columns: repeat(3, 1fr);
        max-height: 700px !important;
      }
    }

    @media (min-width: 1400px) {
      .match-list {
        grid-template-columns: repeat(4, 1fr);
        max-height: 800px !important;
      }
    }
  `;

  async connectedCallback() {
    super.connectedCallback();
    // Load saved format preference
    const savedFormat = localStorage.getItem(
      FORMAT_STORAGE_KEY,
    ) as FileFormat | null;
    if (savedFormat && (savedFormat === "libre" || savedFormat === "dexcom")) {
      this.fileFormat = savedFormat;
    }
    await this._loadExistingDataInfo();
  }

  private _handleFormatChange(format: FileFormat) {
    this.fileFormat = format;
    localStorage.setItem(FORMAT_STORAGE_KEY, format);
    // Re-validate if file already selected
    if (this.selectedFile) {
      this._selectFile(this.selectedFile);
    }
  }

  private async _loadExistingDataInfo() {
    const db = getDb();
    const count = await db.cgmReadings.count();
    if (count > 0) {
      const readings = await db.cgmReadings.orderBy("timestamp").toArray();
      this.existingDataInfo = {
        count,
        dateRange: {
          start: readings[0].timestamp,
          end: readings[readings.length - 1].timestamp,
        },
      };
    }
  }

  private _handleDragOver(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).classList.add("dragging");
  }

  private _handleDragLeave(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).classList.remove("dragging");
  }

  private async _handleDrop(e: DragEvent) {
    e.preventDefault();
    e.stopPropagation();
    (e.currentTarget as HTMLElement).classList.remove("dragging");

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await this._selectFile(files[0]);
    }
  }

  private _handleClick() {
    const input = this.shadowRoot?.querySelector(
      ".file-input",
    ) as HTMLInputElement;
    input?.click();
  }

  private async _handleFileSelect(e: Event) {
    const input = e.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      await this._selectFile(input.files[0]);
    }
  }

  private async _selectFile(file: File) {
    // Validate file type
    if (!file.name.endsWith(".csv")) {
      this.validation = {
        valid: false,
        errors: ["Please select a CSV file"],
        warnings: [],
        recordCount: 0,
        glucoseUnit: "mg/dL",
      };
      return;
    }

    this.selectedFile = file;
    this.validation = null;
    this.importResult = null;
    this.validating = true;

    try {
      const content = await this._readFile(file);
      if (this.fileFormat === "dexcom") {
        this.validation = await this._validateDexcomFile(content);
      } else {
        this.validation = await this._validateLibreViewFile(content);
      }
    } catch (err) {
      this.validation = {
        valid: false,
        errors: [`Failed to read file: ${err}`],
        warnings: [],
        recordCount: 0,
        glucoseUnit: "mg/dL",
      };
    } finally {
      this.validating = false;
    }
  }

  private _readFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = () => reject(reader.error);
      reader.readAsText(file);
    });
  }

  private async _validateLibreViewFile(
    content: string,
  ): Promise<LibreViewValidation> {
    const lines = content.split("\n").filter((l) => l.trim());

    if (lines.length < 2) {
      return {
        valid: false,
        errors: ["File is empty or does not contain data rows"],
        warnings: [],
        recordCount: 0,
        glucoseUnit: "mg/dL",
      };
    }

    // LibreView files often have metadata rows at the top
    // Look for the actual header row (starts with "Device" typically)
    let headerIndex = -1;
    for (let i = 0; i < Math.min(10, lines.length); i++) {
      if (
        lines[i].toLowerCase().includes("device") &&
        lines[i].toLowerCase().includes("timestamp")
      ) {
        headerIndex = i;
        break;
      }
    }

    if (headerIndex === -1) {
      return {
        valid: false,
        errors: [
          "Could not find LibreView header row. Make sure this is a LibreView export file.",
        ],
        warnings: [],
        recordCount: 0,
        glucoseUnit: "mg/dL",
      };
    }

    // Validate header
    const headerValidation = parseLibreViewHeader(lines[headerIndex]);

    if (!headerValidation.valid) {
      return headerValidation;
    }

    // Count data rows and get date range
    const dataRows = lines.slice(headerIndex + 1);
    let validRows = 0;
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    // Build column indices
    const columns = lines[headerIndex]
      .split(",")
      .map((c) => c.trim().replace(/"/g, ""));
    const columnIndices: Record<string, number> = {};
    columns.forEach((col, idx) => {
      columnIndices[col] = idx;
    });

    for (const row of dataRows) {
      if (!row.trim()) continue;

      const parsed = parseLibreViewRow(
        row,
        columnIndices,
        headerValidation.glucoseUnit,
      );
      if (
        parsed &&
        (parsed.historicGlucose !== undefined ||
          parsed.scanGlucose !== undefined)
      ) {
        validRows++;
        const ts = parsed.deviceTimestamp;
        if (!minDate || ts < minDate) minDate = ts;
        if (!maxDate || ts > maxDate) maxDate = ts;
      }
    }

    if (validRows === 0) {
      return {
        valid: false,
        errors: ["No valid glucose readings found in file"],
        warnings: [],
        recordCount: 0,
        glucoseUnit: headerValidation.glucoseUnit,
      };
    }

    return {
      valid: true,
      errors: [],
      warnings: headerValidation.warnings,
      recordCount: validRows,
      dateRange:
        minDate && maxDate ? { start: minDate, end: maxDate } : undefined,
      glucoseUnit: headerValidation.glucoseUnit,
    };
  }

  private async _validateDexcomFile(
    content: string,
  ): Promise<ValidationResult> {
    const lines = content.split("\n").filter((l) => l.trim());

    if (lines.length < 2) {
      return {
        valid: false,
        errors: ["File is empty or does not contain data rows"],
        warnings: [],
        recordCount: 0,
      };
    }

    // Dexcom files start with a header row containing column names
    // Look for the header row
    let headerIndex = -1;
    for (let i = 0; i < Math.min(5, lines.length); i++) {
      const line = lines[i].toLowerCase();
      if (
        line.includes("index") &&
        line.includes("timestamp") &&
        line.includes("event type")
      ) {
        headerIndex = i;
        break;
      }
    }

    if (headerIndex === -1) {
      return {
        valid: false,
        errors: [
          "Could not find Dexcom header row. Make sure this is a Dexcom Clarity export file.",
        ],
        warnings: [],
        recordCount: 0,
      };
    }

    // Validate header
    const headerValidation = parseDexcomHeader(lines[headerIndex]);

    if (!headerValidation.valid) {
      return headerValidation;
    }

    // Build column indices and count data rows
    const columnIndices = buildDexcomColumnIndices(lines[headerIndex]);
    const dataRows = lines.slice(headerIndex + 1);
    let validRows = 0;
    let minDate: Date | null = null;
    let maxDate: Date | null = null;

    for (const row of dataRows) {
      if (!row.trim()) continue;

      const parsed = parseDexcomRow(row, columnIndices);
      if (
        parsed &&
        parsed.eventType === "EGV" &&
        parsed.glucoseValue !== undefined &&
        parsed.timestamp
      ) {
        validRows++;
        const ts = parsed.timestamp;
        if (!minDate || ts < minDate) minDate = ts;
        if (!maxDate || ts > maxDate) maxDate = ts;
      }
    }

    if (validRows === 0) {
      return {
        valid: false,
        errors: ["No valid glucose readings (EGV) found in file"],
        warnings: [],
        recordCount: 0,
      };
    }

    return {
      valid: true,
      errors: [],
      warnings: [],
      recordCount: validRows,
      dateRange:
        minDate && maxDate ? { start: minDate, end: maxDate } : undefined,
    };
  }

  private async _importFile() {
    if (!this.selectedFile || !this.validation?.valid) return;

    this.uploading = true;
    this.progress = 0;

    try {
      const content = await this._readFile(this.selectedFile);
      const result =
        this.fileFormat === "dexcom"
          ? await this._processDexcomFile(content)
          : await this._processLibreViewFile(content);
      this.importResult = result;

      if (result.success) {
        // Refresh existing data info
        await this._loadExistingDataInfo();
      }
    } catch (err) {
      this.importResult = {
        success: false,
        message: `Import failed: ${err}`,
        recordsProcessed: 0,
        recordsImported: 0,
        recordsSkipped: 0,
      };
    } finally {
      this.uploading = false;
    }
  }

  private async _processLibreViewFile(content: string): Promise<ImportResult> {
    const lines = content.split("\n").filter((l) => l.trim());

    // Find header row
    let headerIndex = -1;
    for (let i = 0; i < Math.min(10, lines.length); i++) {
      if (
        lines[i].toLowerCase().includes("device") &&
        lines[i].toLowerCase().includes("timestamp")
      ) {
        headerIndex = i;
        break;
      }
    }

    if (headerIndex === -1) {
      return {
        success: false,
        message: "Could not find header row",
        recordsProcessed: 0,
        recordsImported: 0,
        recordsSkipped: 0,
      };
    }

    // Build column indices
    const columns = lines[headerIndex]
      .split(",")
      .map((c) => c.trim().replace(/"/g, ""));
    const columnIndices: Record<string, number> = {};
    columns.forEach((col, idx) => {
      columnIndices[col] = idx;
    });

    // Parse all rows
    const dataRows = lines.slice(headerIndex + 1);
    const parsedRows: LibreViewRow[] = [];

    for (let i = 0; i < dataRows.length; i++) {
      if (!dataRows[i].trim()) continue;

      const parsed = parseLibreViewRow(
        dataRows[i],
        columnIndices,
        this.validation!.glucoseUnit ?? "mmol/L",
      );
      if (parsed) {
        parsedRows.push(parsed);
      }

      // Update progress
      this.progress = Math.round((i / dataRows.length) * 50);
    }

    // Convert to CGM readings
    const readings = libreViewToCgmReadings(parsedRows);
    this.progress = 60;

    // Store parsed rows for potential reconciliation
    this.parsedLibreViewRows = parsedRows;

    // Check if there are event rows (insulin=4, carbs=5, notes=6)
    const eventRowCount = parsedRows.filter(
      (r) => r.recordType === 4 || r.recordType === 5 || r.recordType === 6,
    ).length;

    // Deduplicate readings within the file (same timestamp can appear multiple times)
    const uniqueReadings = new Map<string, (typeof readings)[0]>();
    for (const reading of readings) {
      uniqueReadings.set(reading.id, reading);
    }
    const deduplicatedReadings = Array.from(uniqueReadings.values());

    // Get count of existing readings to calculate new imports
    const existingIds = new Set(
      (await getDb().cgmReadings.toArray()).map((r) => r.id),
    );
    const newCount = deduplicatedReadings.filter(
      (r) => !existingIds.has(r.id),
    ).length;
    this.progress = 80;

    // Bulk upsert readings (insert or update) with sync status
    if (deduplicatedReadings.length > 0) {
      await saveCgmReadingsBatch(deduplicatedReadings);
    }
    this.progress = 100;

    // Get date range
    let dateRange: { start: Date; end: Date } | undefined;
    if (readings.length > 0) {
      const sorted = readings.sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
      );
      dateRange = {
        start: sorted[0].timestamp,
        end: sorted[sorted.length - 1].timestamp,
      };
    }

    const eventMessage =
      eventRowCount > 0
        ? ` Found ${eventRowCount} event rows available for reconciliation.`
        : "";

    return {
      success: true,
      message:
        newCount > 0
          ? `Successfully imported ${newCount} new readings.${eventMessage}`
          : `No new readings to import (all data already exists).${eventMessage}`,
      recordsProcessed: deduplicatedReadings.length,
      recordsImported: newCount,
      recordsSkipped: deduplicatedReadings.length - newCount,
      dateRange,
    };
  }

  private async _processDexcomFile(content: string): Promise<ImportResult> {
    const lines = content.split("\n").filter((l) => l.trim());

    // Find header row
    let headerIndex = -1;
    for (let i = 0; i < Math.min(5, lines.length); i++) {
      const line = lines[i].toLowerCase();
      if (
        line.includes("index") &&
        line.includes("timestamp") &&
        line.includes("event type")
      ) {
        headerIndex = i;
        break;
      }
    }

    if (headerIndex === -1) {
      return {
        success: false,
        message: "Could not find Dexcom header row",
        recordsProcessed: 0,
        recordsImported: 0,
        recordsSkipped: 0,
      };
    }

    // Build column indices
    const columnIndices = buildDexcomColumnIndices(lines[headerIndex]);

    // Parse all rows
    const dataRows = lines.slice(headerIndex + 1);
    const parsedRows: DexcomRow[] = [];

    for (let i = 0; i < dataRows.length; i++) {
      if (!dataRows[i].trim()) continue;

      const parsed = parseDexcomRow(dataRows[i], columnIndices);
      if (parsed) {
        parsedRows.push(parsed);
      }

      // Update progress
      this.progress = Math.round((i / dataRows.length) * 50);
    }

    // Convert to CGM readings
    const readings = dexcomToCgmReadings(parsedRows);
    this.progress = 60;

    // Deduplicate readings within the file
    const uniqueReadings = new Map<string, (typeof readings)[0]>();
    for (const reading of readings) {
      uniqueReadings.set(reading.id, reading);
    }
    const deduplicatedReadings = Array.from(uniqueReadings.values());

    // Get count of existing readings to calculate new imports
    const existingIds = new Set(
      (await getDb().cgmReadings.toArray()).map((r) => r.id),
    );
    const newCount = deduplicatedReadings.filter(
      (r) => !existingIds.has(r.id),
    ).length;
    this.progress = 80;

    // Bulk upsert readings with sync status
    if (deduplicatedReadings.length > 0) {
      await saveCgmReadingsBatch(deduplicatedReadings);
    }
    this.progress = 100;

    // Get date range
    let dateRange: { start: Date; end: Date } | undefined;
    if (readings.length > 0) {
      const sorted = readings.sort(
        (a, b) => a.timestamp.getTime() - b.timestamp.getTime(),
      );
      dateRange = {
        start: sorted[0].timestamp,
        end: sorted[sorted.length - 1].timestamp,
      };
    }

    return {
      success: true,
      message:
        newCount > 0
          ? `Successfully imported ${newCount} new readings`
          : "No new readings to import (all data already exists)",
      recordsProcessed: deduplicatedReadings.length,
      recordsImported: newCount,
      recordsSkipped: deduplicatedReadings.length - newCount,
      dateRange,
    };
  }

  private _hasEventRows(): boolean {
    return this.parsedLibreViewRows.some(
      (r) => r.recordType === 4 || r.recordType === 5 || r.recordType === 6,
    );
  }

  private async _runReconciliation(): Promise<void> {
    if (this.parsedLibreViewRows.length === 0) return;

    this.reconciling = true;
    try {
      // Get existing entries and CGM readings
      const db = getDb();
      const existingEntries = await db.entries.toArray();
      const cgmReadings = await db.cgmReadings.toArray();
      const settingsRecord = await db.settings.get("settings");

      // Run reconciliation
      const result = reconcileLibreViewData(
        this.parsedLibreViewRows,
        existingEntries,
        cgmReadings,
        settingsRecord ?? undefined,
      );

      this.reconciliationResult = result;
      this.showReconciliation = true;
      this.reconciliationMealTimes =
        settingsRecord?.mealTimes ?? DEFAULT_MEAL_TIMES;

      // Initialize all decisions as "accept" by default
      this.reconciliationDecisions = new Map();
      result.matches.forEach((match, index) => {
        if (match.suggestedAction !== "skip") {
          this.reconciliationDecisions.set(index, "accept");
        }
      });
    } catch (error) {
      console.error("Reconciliation failed:", error);
    } finally {
      this.reconciling = false;
    }
  }

  private _renderReconciliation() {
    if (!this.reconciliationResult) return html``;

    const { matches, summary } = this.reconciliationResult;

    return html`
      <div class="reconciliation-panel" style="margin-top: 1.5rem;">
        <h3
          style="margin: 0 0 1rem; display: flex; align-items: center; gap: 0.5rem;"
        >
          <sl-icon name="arrow-left-right"></sl-icon>
          Event Reconciliation
        </h3>

        <div
          class="reconciliation-summary"
          style="display: grid; grid-template-columns: repeat(5, 1fr); gap: 0.5rem; margin-bottom: 1rem; padding: 0.75rem; background: #f8f9fa; border-radius: 8px; font-size: 0.75rem;"
        >
          <div style="text-align: center;">
            <div style="font-weight: 600; color: #22c55e;">
              ${summary.inserts}
            </div>
            <div style="color: #6c757d;">New</div>
          </div>
          <div style="text-align: center;">
            <div style="font-weight: 600; color: #3b82f6;">
              ${summary.updates}
            </div>
            <div style="color: #6c757d;">Update</div>
          </div>
          <div style="text-align: center;">
            <div style="font-weight: 600; color: #8b5cf6;">
              ${summary.merges}
            </div>
            <div style="color: #6c757d;">Merge</div>
          </div>
          <div style="text-align: center;">
            <div style="font-weight: 600; color: #f59e0b;">
              ${summary.reviews}
            </div>
            <div style="color: #6c757d;">Review</div>
          </div>
          <div style="text-align: center;">
            <div style="font-weight: 600; color: #6c757d;">
              ${summary.skips}
            </div>
            <div style="color: #6c757d;">Skip</div>
          </div>
        </div>

        <div
          class="match-list"
          style="max-height: 400px; overflow-y: auto; border: 1px solid #dee2e6; border-radius: 8px;"
        >
          ${matches.map((match, index) => this._renderMatch(match, index))}
        </div>

        <div
          class="reconciliation-actions"
          style="margin-top: 1rem; display: flex; gap: 0.5rem; justify-content: flex-end;"
        >
          <sl-button
            variant="default"
            size="small"
            @click=${() => {
              this.showReconciliation = false;
              this.reconciliationResult = null;
            }}
          >
            Cancel
          </sl-button>
          <sl-button
            variant="primary"
            size="small"
            @click=${this._applyReconciliation}
          >
            <sl-icon slot="prefix" name="check-lg"></sl-icon>
            Apply ${this._getAcceptedCount()} Changes
          </sl-button>
        </div>
      </div>
    `;
  }

  private _renderMatch(match: ReconciliationMatch, index: number) {
    const { candidate, existingEntry, suggestedAction, confidence } = match;
    const decision = this.reconciliationDecisions.get(index);

    // Compute suggested period tag for display
    const periodSuggestion = suggestPeriodTagSmart(
      candidate.timestamp,
      this.reconciliationMealTimes,
      [],
    );
    const suggestedPeriod = periodSuggestion.periodTag;

    const actionColors: Record<string, string> = {
      insert: "#22c55e",
      update: "#3b82f6",
      merge: "#8b5cf6",
      review: "#f59e0b",
      skip: "#6c757d",
    };

    const actionIcons: Record<string, string> = {
      insert: "plus-circle",
      update: "arrow-clockwise",
      merge: "arrow-left-right",
      review: "question-circle",
      skip: "dash-circle",
    };

    // Helper to format values
    const formatVal = (val: number | undefined, decimals = 1) =>
      val !== undefined ? val.toFixed(decimals) : "-";

    // Build rows - only show rows where at least one side has data
    const rows: { label: string; cgm: string; dafne: string }[] = [];

    // Time row
    rows.push({
      label: DAFNE_LABELS.TIME,
      cgm: format(candidate.timestamp, "HH:mm"),
      dafne: existingEntry ? format(existingEntry.timestamp, "HH:mm") : "-",
    });

    // GL row
    if (
      candidate.bloodGlucose !== undefined ||
      existingEntry?.bloodGlucose !== undefined
    ) {
      rows.push({
        label: DAFNE_LABELS.GL,
        cgm: formatVal(candidate.bloodGlucose),
        dafne: formatVal(existingEntry?.bloodGlucose),
      });
    }

    // CP row (carbs in grams)
    if (candidate.carbs !== undefined || existingEntry?.carbs !== undefined) {
      rows.push({
        label: DAFNE_LABELS.CP,
        cgm: candidate.carbs !== undefined ? `${candidate.carbs}g` : "-",
        dafne:
          existingEntry?.carbs !== undefined ? `${existingEntry.carbs}g` : "-",
      });
    }

    // Quick row (quick acting insulin)
    if (
      candidate.qaBase !== undefined ||
      candidate.qaCorrection !== undefined ||
      existingEntry?.qaBase !== undefined ||
      existingEntry?.qaCorrection !== undefined
    ) {
      rows.push({
        label: DAFNE_LABELS.QUICK,
        cgm:
          candidate.qaBase !== undefined || candidate.qaCorrection !== undefined
            ? formatInsulinDose(candidate.qaBase, candidate.qaCorrection)
            : "-",
        dafne:
          existingEntry?.qaBase !== undefined ||
          existingEntry?.qaCorrection !== undefined
            ? formatInsulinDose(
                existingEntry.qaBase,
                existingEntry.qaCorrection,
              )
            : "-",
      });
    }

    // Long row (background insulin)
    if (
      candidate.biBase !== undefined ||
      candidate.biCorrection !== undefined ||
      existingEntry?.biBase !== undefined ||
      existingEntry?.biCorrection !== undefined
    ) {
      rows.push({
        label: DAFNE_LABELS.LONG,
        cgm:
          candidate.biBase !== undefined || candidate.biCorrection !== undefined
            ? formatInsulinDose(candidate.biBase, candidate.biCorrection)
            : "-",
        dafne:
          existingEntry?.biBase !== undefined ||
          existingEntry?.biCorrection !== undefined
            ? formatInsulinDose(
                existingEntry.biBase,
                existingEntry.biCorrection,
              )
            : "-",
      });
    }

    // Ketones row
    if (
      candidate.ketones !== undefined ||
      existingEntry?.ketones !== undefined
    ) {
      rows.push({
        label: DAFNE_LABELS.KET,
        cgm: formatVal(candidate.ketones),
        dafne: formatVal(existingEntry?.ketones),
      });
    }

    // Notes row
    if (candidate.notes || existingEntry?.notes) {
      rows.push({
        label: DAFNE_LABELS.NOTE,
        cgm: candidate.notes || "-",
        dafne: existingEntry?.notes || "-",
      });
    }

    // Exercise row
    if (candidate.exercise || existingEntry?.exercise) {
      const formatExercise = (ex?: {
        type: string;
        intensity?: string;
        duration?: number;
      }) => {
        if (!ex) return "-";
        const parts = [ex.type];
        if (ex.intensity) parts.push(ex.intensity);
        if (ex.duration) parts.push(`${ex.duration}min`);
        return parts.join(", ");
      };
      rows.push({
        label: DAFNE_LABELS.EXERCISE,
        cgm: formatExercise(candidate.exercise),
        dafne: formatExercise(existingEntry?.exercise),
      });
    }

    return html`
      <div
        class="match-item"
        style="padding: 0.75rem; ${decision === "skip" ? "opacity: 0.5;" : ""}"
      >
        <div
          style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;"
        >
          <div style="display: flex; align-items: center; gap: 0.5rem;">
            <sl-icon
              name=${actionIcons[suggestedAction]}
              style="color: ${actionColors[suggestedAction]};"
            ></sl-icon>
            <span style="font-weight: 500; text-transform: capitalize;"
              >${suggestedAction}</span
            >
            ${confidence && confidence !== "none"
              ? html`<sl-badge
                  variant="${confidence === "exact" || confidence === "high"
                    ? "success"
                    : confidence === "medium"
                      ? "warning"
                      : "danger"}"
                  pill
                  >${confidence}</sl-badge
                >`
              : suggestedAction === "insert"
                ? html`<sl-badge variant="success" pill>new</sl-badge>`
                : ""}
          </div>
          <span
            style="font-size: 0.75rem; color: #6c757d; display: flex; align-items: center; gap: 0.5rem;"
          >
            ${suggestedPeriod && suggestedAction !== "skip"
              ? html`<sl-badge variant="neutral" pill
                  >${suggestedPeriod}</sl-badge
                >`
              : ""}
            ${format(candidate.timestamp, "dd MMM")}
          </span>
        </div>

        <table
          style="width: 100%; border-collapse: collapse; font-size: 0.8125rem;"
        >
          <thead>
            <tr style="border-bottom: 2px solid #dee2e6;">
              <th
                style="text-align: left; padding: 0.25rem 0.5rem; font-weight: 500; width: 50px;"
              ></th>
              <th
                style="text-align: right; padding: 0.25rem 0.5rem; font-weight: 600; color: #15803d; background: #f0fdf4;"
              >
                CGM
              </th>
              <th
                style="text-align: right; padding: 0.25rem 0.5rem; font-weight: 600; color: #1d4ed8; background: #eff6ff;"
              >
                DAFNE
              </th>
            </tr>
          </thead>
          <tbody>
            ${rows.map(
              (row) => html`
                <tr style="border-bottom: 1px solid #f0f0f0;">
                  <td
                    style="padding: 0.25rem 0.5rem; font-weight: 500; color: #6c757d;"
                  >
                    ${row.label}
                  </td>
                  <td
                    style="text-align: right; padding: 0.25rem 0.5rem; background: #f0fdf4;"
                  >
                    ${row.cgm}
                  </td>
                  <td
                    style="text-align: right; padding: 0.25rem 0.5rem; background: #eff6ff;"
                  >
                    ${row.dafne}
                  </td>
                </tr>
              `,
            )}
          </tbody>
        </table>

        ${suggestedAction !== "skip"
          ? html`
              <div style="margin-top: 0.5rem; display: flex; gap: 0.5rem;">
                <sl-button
                  size="small"
                  variant=${decision === "accept" ? "success" : "default"}
                  @click=${() => this._setDecision(index, "accept")}
                  >${decision === "accept" ? "Accepted" : "Accept"}</sl-button
                >
                <sl-button
                  size="small"
                  variant=${decision === "skip" ? "neutral" : "default"}
                  @click=${() => this._setDecision(index, "skip")}
                  >${decision === "skip" ? "Skipping" : "Skip"}</sl-button
                >
              </div>
            `
          : ""}
      </div>
    `;
  }

  private _setDecision(
    index: number,
    decision: "accept" | "skip" | "edit",
  ): void {
    this.reconciliationDecisions = new Map(this.reconciliationDecisions);
    this.reconciliationDecisions.set(index, decision);
  }

  private _getAcceptedCount(): number {
    let count = 0;
    this.reconciliationDecisions.forEach((decision) => {
      if (decision === "accept") count++;
    });
    return count;
  }

  private async _applyReconciliation(): Promise<void> {
    if (!this.reconciliationResult) return;

    const { matches } = this.reconciliationResult;
    let applied = 0;

    // Get settings and existing entries for smart period suggestion
    const db = getDb();
    const settingsRecord = await db.settings.get("settings");
    const mealTimes = settingsRecord?.mealTimes ?? DEFAULT_MEAL_TIMES;
    const allEntries = await db.entries.toArray();

    for (let i = 0; i < matches.length; i++) {
      const decision = this.reconciliationDecisions.get(i);
      if (decision !== "accept") continue;

      const match = matches[i];
      const { candidate, existingEntry, suggestedAction } = match;

      try {
        if (suggestedAction === "insert") {
          // Get existing periods for the same day for smart period suggestion
          const sameDayEntries = allEntries.filter((e) => {
            const entryDate = new Date(e.timestamp);
            return (
              entryDate.toDateString() === candidate.timestamp.toDateString()
            );
          });
          const existingPeriods = sameDayEntries
            .map((e) => e.periodTag)
            .filter(Boolean) as PeriodTag[];

          // Create new entry from candidate
          const periodSuggestion = suggestPeriodTagSmart(
            candidate.timestamp,
            mealTimes,
            existingPeriods,
          );
          const newEntry: LogEntry = {
            id: crypto.randomUUID(),
            timestamp: candidate.timestamp,
            periodTag: periodSuggestion.periodTag,
            bloodGlucose: candidate.bloodGlucose,
            carbs: candidate.carbs,
            qaBase: candidate.qaBase,
            qaCorrection: candidate.qaCorrection,
            biBase: candidate.biBase,
            biCorrection: candidate.biCorrection,
            ketones: candidate.ketones,
            notes: candidate.notes,
            exercise: candidate.exercise,
            createdAt: new Date(),
            updatedAt: new Date(),
          };
          // Use saveEntry from operations.ts to ensure sync metadata is set
          await saveEntry(newEntry);
          applied++;
        } else if (
          (suggestedAction === "update" || suggestedAction === "merge") &&
          existingEntry
        ) {
          // Update existing entry with candidate data (fill missing fields)
          const updates: Partial<LogEntry> = {
            updatedAt: new Date(),
          };
          if (
            candidate.qaBase !== undefined &&
            existingEntry.qaBase === undefined
          ) {
            updates.qaBase = candidate.qaBase;
          }
          if (
            candidate.qaCorrection !== undefined &&
            existingEntry.qaCorrection === undefined
          ) {
            updates.qaCorrection = candidate.qaCorrection;
          }
          if (
            candidate.biBase !== undefined &&
            existingEntry.biBase === undefined
          ) {
            updates.biBase = candidate.biBase;
          }
          if (
            candidate.biCorrection !== undefined &&
            existingEntry.biCorrection === undefined
          ) {
            updates.biCorrection = candidate.biCorrection;
          }
          if (
            candidate.carbs !== undefined &&
            existingEntry.carbs === undefined
          ) {
            updates.carbs = candidate.carbs;
          }
          if (
            candidate.bloodGlucose !== undefined &&
            existingEntry.bloodGlucose === undefined
          ) {
            updates.bloodGlucose = candidate.bloodGlucose;
          }
          if (
            candidate.ketones !== undefined &&
            existingEntry.ketones === undefined
          ) {
            updates.ketones = candidate.ketones;
          }
          if (candidate.notes && !existingEntry.notes) {
            updates.notes = candidate.notes;
          }
          if (candidate.exercise && !existingEntry.exercise) {
            updates.exercise = candidate.exercise;
          }

          // Use updateEntry from operations.ts to ensure sync metadata is set
          await updateEntry(existingEntry.id, updates);
          applied++;
        }
      } catch (error) {
        console.error(`Failed to apply change ${i}:`, error);
      }
    }

    // Update UI
    this.showReconciliation = false;
    this.reconciliationResult = null;
    this.importResult = {
      ...this.importResult!,
      message: `${this.importResult!.message} Applied ${applied} event reconciliations.`,
    };
  }

  private _reset() {
    this.selectedFile = null;
    this.validation = null;
    this.importResult = null;
    this.progress = 0;
    this.showReconciliation = false;
    this.reconciliationResult = null;
    this.reconciliationDecisions = new Map();
    this.parsedLibreViewRows = [];

    // Reset file input
    const input = this.shadowRoot?.querySelector(
      ".file-input",
    ) as HTMLInputElement;
    if (input) input.value = "";
  }

  private _formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  render() {
    return html`
      <app-shell pageTitle="Import Data">
        <div class="upload-content">
          <div class="page-header">
            <h1>Import CGM Data</h1>
            <p>
              Upload your CGM data to see glucose trends alongside your log
              entries
            </p>
          </div>

          ${this.existingDataInfo
            ? html`
                <div class="existing-data">
                  <h4>📊 Existing CGM Data</h4>
                  <p>
                    ${this.existingDataInfo.count.toLocaleString()} readings
                    ${this.existingDataInfo.dateRange
                      ? html`
                          from
                          ${format(
                            this.existingDataInfo.dateRange.start,
                            "dd MMM yyyy",
                          )}
                          to
                          ${format(
                            this.existingDataInfo.dateRange.end,
                            "dd MMM yyyy",
                          )}
                        `
                      : ""}
                  </p>
                </div>
              `
            : ""}

          <sl-card>
            <div class="format-selector">
              <label>CGM Source</label>
              <sl-radio-group
                value=${this.fileFormat}
                @sl-change=${(e: Event) =>
                  this._handleFormatChange(
                    (e.target as HTMLInputElement).value as FileFormat,
                  )}
              >
                <sl-radio-button value="libre">Libre</sl-radio-button>
                <sl-radio-button value="dexcom">Dexcom</sl-radio-button>
              </sl-radio-group>
            </div>

            <div class="libreview-info">
              ${this.fileFormat === "libre"
                ? html`
                    <h4>📥 How to export from LibreView</h4>
                    <ol>
                      <li>Go to <strong>libreview.com</strong> and log in</li>
                      <li>Click on <strong>Reports</strong></li>
                      <li>
                        Select <strong>Export Data</strong> or
                        <strong>Download CSV</strong>
                      </li>
                      <li>Save the file and upload it here</li>
                    </ol>
                  `
                : html`
                    <h4>📥 How to export from Dexcom Clarity</h4>
                    <ol>
                      <li>
                        Go to <strong>clarity.dexcom.com</strong> and log in
                      </li>
                      <li>Click on <strong>Reports</strong> at the top</li>
                      <li>
                        Select a date range and click <strong>Export</strong>
                      </li>
                      <li>Choose <strong>CSV</strong> format and download</li>
                      <li>Upload the CSV file here</li>
                    </ol>
                  `}
            </div>

            ${this.fileFormat === "libre"
              ? html`
                  <div class="notation-guide">
                    <details>
                      <summary>
                        📝 DAFNE Notation Guide for LibreView Notes
                      </summary>
                      <div class="guide-content">
                        <p>
                          Add these patterns to LibreView notes for automatic
                          parsing during import:
                        </p>

                        <h5>Exercise</h5>
                        <ul>
                          <li>
                            <code>ex 5 30</code> - medium intensity (5/10), 30
                            min
                          </li>
                          <li>
                            <code>ex 2 15</code> - light intensity (2/10), 15
                            min
                          </li>
                          <li>
                            <code>ex 9 45</code> - hard intensity (9/10), 45 min
                          </li>
                          <li>
                            <code>ex easy 20</code> or <code>ex light 20</code>
                          </li>
                          <li>
                            <code>ex med 30</code> or
                            <code>ex moderate 30</code>
                          </li>
                          <li>
                            <code>ex hard 45</code> or
                            <code>ex vigorous 45</code>
                          </li>
                        </ul>
                        <p
                          style="font-size: 0.75rem; color: var(--text-secondary); margin-top: 0.25rem;"
                        >
                          Intensity: 1-3 = light, 4-7 = moderate, 8-10 =
                          vigorous
                        </p>

                        <h5>Carbs & Insulin</h5>
                        <ul>
                          <li>
                            <code>3.5cp + 2cr</code> - 3.5 CP plus 2 units
                            correction
                          </li>
                          <li>
                            <code>4cp - 1cr</code> - 4 CP minus 1 unit
                            correction
                          </li>
                          <li>
                            <code>30g carbs</code> or <code>30g</code> - grams
                            of carbs
                          </li>
                        </ul>

                        <h5>Full Dose Breakdown</h5>
                        <ul>
                          <li>
                            <code>7cp = 21u + 1cr</code> - 7 CP = 21 units base
                            + 1 correction
                          </li>
                        </ul>

                        <h5>Other</h5>
                        <ul>
                          <li>
                            <code>GL 8.5</code> or <code>BG 8.5</code> - blood
                            glucose
                          </li>
                          <li><code>ket 0.5</code> - ketone reading</li>
                        </ul>
                      </div>
                    </details>
                  </div>
                `
              : ""}
            ${!this.importResult
              ? html`
                  <div
                    class="upload-section"
                    @dragover=${this._handleDragOver}
                    @dragleave=${this._handleDragLeave}
                    @drop=${this._handleDrop}
                    @click=${this._handleClick}
                  >
                    <input
                      type="file"
                      class="file-input"
                      accept=".csv"
                      @change=${this._handleFileSelect}
                    />
                    <sl-icon name="cloud-upload"></sl-icon>
                    <h3>
                      Upload
                      ${this.fileFormat === "libre" ? "LibreView" : "Dexcom"}
                      CSV
                    </h3>
                    <p>Drag and drop your file here, or click to browse</p>
                  </div>

                  ${this.selectedFile
                    ? html`
                        <div class="selected-file">
                          <sl-icon name="file-earmark-text"></sl-icon>
                          <div class="file-info">
                            <div class="name">${this.selectedFile.name}</div>
                            <div class="size">
                              ${this._formatFileSize(this.selectedFile.size)}
                            </div>
                          </div>
                          <sl-button
                            variant="text"
                            size="small"
                            @click=${this._reset}
                          >
                            <sl-icon name="x-lg"></sl-icon>
                          </sl-button>
                        </div>
                      `
                    : ""}
                  ${this.validating
                    ? html`
                        <div style="text-align: center; margin-top: 1rem;">
                          <sl-spinner></sl-spinner>
                          <p>Validating file...</p>
                        </div>
                      `
                    : ""}
                  ${this.validation
                    ? html`
                        <div class="validation-results">
                          ${this.validation.errors.map(
                            (err) => html`
                              <div class="validation-item error">
                                <sl-icon name="x-circle-fill"></sl-icon>
                                <span>${err}</span>
                              </div>
                            `,
                          )}
                          ${this.validation.warnings.map(
                            (warn) => html`
                              <div class="validation-item warning">
                                <sl-icon
                                  name="exclamation-triangle-fill"
                                ></sl-icon>
                                <span>${warn}</span>
                              </div>
                            `,
                          )}
                          ${this.validation.valid
                            ? html`
                                <div class="validation-item success">
                                  <sl-icon name="check-circle-fill"></sl-icon>
                                  <span>Valid LibreView file detected</span>
                                </div>
                                <div class="validation-item success">
                                  <sl-icon name="check-circle-fill"></sl-icon>
                                  <span
                                    >${this.validation.recordCount.toLocaleString()}
                                    glucose readings found</span
                                  >
                                </div>
                                ${this.validation.dateRange
                                  ? html`
                                      <div class="validation-item success">
                                        <sl-icon
                                          name="calendar-check"
                                        ></sl-icon>
                                        <span>
                                          Date range:
                                          ${format(
                                            this.validation.dateRange.start,
                                            "dd MMM yyyy",
                                          )}
                                          to
                                          ${format(
                                            this.validation.dateRange.end,
                                            "dd MMM yyyy",
                                          )}
                                        </span>
                                      </div>
                                    `
                                  : ""}
                                <div class="validation-item success">
                                  <sl-icon name="info-circle"></sl-icon>
                                  <span
                                    >Glucose unit:
                                    ${this.validation.glucoseUnit}</span
                                  >
                                </div>
                              `
                            : ""}
                        </div>

                        ${this.validation.valid && !this.uploading
                          ? html`
                              <div class="actions">
                                <sl-button
                                  variant="default"
                                  @click=${this._reset}
                                  >Cancel</sl-button
                                >
                                <sl-button
                                  variant="primary"
                                  @click=${this._importFile}
                                >
                                  <sl-icon
                                    slot="prefix"
                                    name="download"
                                  ></sl-icon>
                                  Import Data
                                </sl-button>
                              </div>
                            `
                          : ""}
                      `
                    : ""}
                  ${this.uploading
                    ? html`
                        <sl-progress-bar
                          value=${this.progress}
                        ></sl-progress-bar>
                        <p style="text-align: center; color: #6c757d;">
                          ${this.progress < 50
                            ? "Parsing file..."
                            : this.progress < 80
                              ? "Processing readings..."
                              : "Saving to database..."}
                        </p>
                      `
                    : ""}
                `
              : html`
                  ${this.importResult.success
                    ? html`
                        <sl-alert variant="success" open>
                          <sl-icon
                            slot="icon"
                            name="check-circle-fill"
                          ></sl-icon>
                          ${this.importResult.message}
                        </sl-alert>
                      `
                    : html`
                        <sl-alert variant="danger" open>
                          <sl-icon slot="icon" name="x-circle-fill"></sl-icon>
                          ${this.importResult.message}
                        </sl-alert>
                      `}
                  ${this.importResult.success
                    ? html` <div
                          class="import-summary"
                          style="margin-top: 1rem;"
                        >
                          <div class="stat">
                            <span class="label">Records processed</span>
                            <span
                              >${this.importResult.recordsProcessed.toLocaleString()}</span
                            >
                          </div>
                          <div class="stat">
                            <span class="label">New records imported</span>
                            <span
                              >${this.importResult.recordsImported.toLocaleString()}</span
                            >
                          </div>
                          <div class="stat">
                            <span class="label">Duplicates skipped</span>
                            <span
                              >${this.importResult.recordsSkipped.toLocaleString()}</span
                            >
                          </div>
                          ${this.importResult.dateRange
                            ? html`
                                <div class="stat">
                                  <span class="label">Date range</span>
                                  <span>
                                    ${format(
                                      this.importResult.dateRange.start,
                                      "dd MMM",
                                    )}
                                    -
                                    ${format(
                                      this.importResult.dateRange.end,
                                      "dd MMM yyyy",
                                    )}
                                  </span>
                                </div>
                              `
                            : ""}
                        </div>
                        ${this._hasEventRows() && !this.showReconciliation
                          ? html`
                              <div
                                class="reconciliation-prompt"
                                style="margin-top: 1rem; padding: 1rem; background: #f0f9ff; border-radius: 8px; border: 1px solid #0284c7;"
                              >
                                <p style="margin: 0 0 0.75rem; color: #0369a1;">
                                  <sl-icon
                                    name="journal-text"
                                    style="vertical-align: middle;"
                                  ></sl-icon>
                                  <strong>Event Data Available</strong>
                                </p>
                                <p
                                  style="margin: 0 0 0.75rem; font-size: 0.875rem; color: #475569;"
                                >
                                  Found insulin doses, carbs, and notes that can
                                  be reconciled with your DAFNE log entries.
                                </p>
                                <sl-button
                                  variant="primary"
                                  size="small"
                                  @click=${this._runReconciliation}
                                  ?loading=${this.reconciling}
                                >
                                  <sl-icon
                                    slot="prefix"
                                    name="arrow-left-right"
                                  ></sl-icon>
                                  Reconcile Events
                                </sl-button>
                              </div>
                            `
                          : ""}
                        ${this.showReconciliation
                          ? this._renderReconciliation()
                          : ""}`
                    : ""}

                  <div class="actions">
                    <sl-button variant="primary" @click=${this._reset}>
                      <sl-icon slot="prefix" name="upload"></sl-icon>
                      Upload Another File
                    </sl-button>
                  </div>
                `}
          </sl-card>
        </div>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "upload-page": UploadPage;
  }
}
